import { useRouter } from 'next/router'
import { ReactComponent as ArrowPreviousIcon } from '@svgs/keyline/arrow-previous.svg'

import useIsReactClient from '@helpers/hooks/useIsReactClient'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { useWaitForUserProfile } from '@api/accounts/auth/hooks/useWaitForUserProfile'

import { ACCOUNT_PAGE_NAME } from '@api/accounts/auth/consts'
import { prefixLocale } from '@helpers/env'

import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import LayoutAccountLoggedOutModal from '@components/Layout/LayoutModals/LayoutAccountLoggedOutModal'

import styles from './styles.module.scss'
import HeroBanner from './shared/HeroBanner'
import Sidebar from './shared/Sidebar'
import Navigation from './shared/Navigation'
import LoginLink from './shared/LoginLink'
import ErrorModal from '@components/Layout/LayoutModals/ErrorModal'

type AccountWrapperProps = {
  children: React.ReactNode
  withSidebar?: boolean
}

export const ACCOUNT_ANALYTICS_EVENT_LABEL_NAMES = {
  [`/${ACCOUNT_PAGE_NAME}?page=overview`]: 'Overview',
  [`/${ACCOUNT_PAGE_NAME}?page=orders`]: 'Orders',
  [`/${ACCOUNT_PAGE_NAME}?page=your-fit`]: 'Your Fit',
  [`/${ACCOUNT_PAGE_NAME}?page=account-details`]: 'Account Details'
}

export function AccountWrapper({
  children,
  withSidebar = true
}: AccountWrapperProps) {
  const { userProfile, errorMessage } = useAuth()
  const { isReady: isLoggedIn, isLoading } = useWaitForUserProfile(userProfile)
  const router = useRouter()
  const isReactClient = useIsReactClient()
  const pageHande = router.query?.page as string
  const pageTitle = Object.entries(ACCOUNT_ANALYTICS_EVENT_LABEL_NAMES).find(
    ([path]) => path.includes(pageHande)
  )?.[1]

  const showLoggedOutModal = isReactClient && !isLoggedIn && !isLoading
  const showPageTitle =
    isReactClient &&
    pageTitle &&
    !router.query?.orderId &&
    !/overview/.test(pageHande)
  const showOrderBackButton = pageHande?.toLowerCase() === 'orders-page'

  const goToOrders = () => {
    router.push(
      {
        href: `${prefixLocale()}/account?page=orders`,
        query: { page: 'orders' }
      },
      `${prefixLocale()}/account?page=orders`,
      {
        scroll: false,
        shallow: true
      }
    )
  }

  return (
    <div className={styles.AccountWrapper}>
      {showLoggedOutModal && !errorMessage && <LayoutAccountLoggedOutModal />}
      {errorMessage && <ErrorModal />}
      <HeroBanner />
      <div className={styles.AccountWrapper__content}>
        <Navigation />
        <div className={styles.AccountWrapper__title}>
          {showPageTitle && <h1>{pageTitle}</h1>}
          {showOrderBackButton && (
            <ButtonMinimal onClick={goToOrders} classes={styles.backButton}>
              <ArrowPreviousIcon /> Back to orders
            </ButtonMinimal>
          )}
        </div>
        <div className={styles.AccountWrapper__grid}>
          {children}
          {withSidebar && <Sidebar type={pageTitle} />}
        </div>
        <div className={styles.AccountWrapper__logout}>
          <LoginLink />
        </div>
      </div>
    </div>
  )
}
